<template>
    <purchase-contract-section>
        <template #title>
            <div
                ref="root"
                class="flex w-full flex-col items-start justify-between md:flex-row md:items-center"
            >
                <div>
                    {{
                        $t('purchaseContract.planDetails', {
                            planType: planData.friendlyPlanType
                        })
                    }}
                </div>

                <client-only>
                    <div
                        class="text-right text-base font-medium text-green-600"
                        v-html="expirationRelativeString"
                    />
                </client-only>
            </div>
        </template>

        <div
            v-if="!isActiveSection"
            class="flex w-full justify-end"
        >
            <prime-button @click="emit('editSection')">
                <div class="flex items-center gap-2">
                    <i class="pi pi-pencil" />
                    <span>{{ $t('edit') }}</span>
                </div>
            </prime-button>
        </div>

        <participant-details-completed
            v-if="!isActiveSection"
            :contract="contractData"
        />

        <div
            v-else
            class="space-y-6"
        >
            <enrollment-customer-picker
                v-if="!allowEmptyEnrollments"
                v-model:enrollment-inputs="enrollmentInputFormData"
                :contract="contractData"
                :show-errors="showErrors"
                :is-guest="contractData.isGuest"
                :enrollment-constraints="enrollmentConstraints"
                :class="
                    enrollmentInputInvalid
                        ? 'js-ParticipantDetailsFormError'
                        : ''
                "
            />

            <customer-input
                v-if="collectCustomerInformation"
                v-model:customer-data="customerFormData"
                :enrollment-inputs="enrollmentInputFormData"
                :facility="contractData.facility"
                :contract-holder-minimum-age="
                    planData.terms.contractHolderMinimumAge
                "
                :show-email="
                    !isKiosk ||
                    !contractData.isGuest ||
                    planData.terms.askForEmailForGuestAtKiosk
                "
                :required-email="
                    !contractData.isGuest ||
                    (planData.terms.askForEmailForGuestAtKiosk &&
                        !planData.terms.optionalEmailForGuestAtKiosk)
                "
                :show-phone="
                    !isKiosk ||
                    !contractData.isGuest ||
                    planData.terms.askForPhoneForGuestAtKiosk
                "
                :required-phone="
                    contractData.isGuest &&
                    planData.terms.askForPhoneForGuestAtKiosk &&
                    planData.terms.requiredPhoneForGuestAtKiosk
                "
                :is-guest="contractData.isGuest"
                :highlight-errors="showErrors"
                :class="
                    customerFormDataInvalid
                        ? 'js-ParticipantDetailsFormError'
                        : ''
                "
            />

            <div
                v-if="addressRequired"
                :class="
                    addressFormDataInvalid
                        ? 'js-ParticipantDetailsFormError'
                        : ''
                "
            >
                <address-input-component
                    v-model:addressData="addressFormData"
                    :show-error-messages="showErrors"
                    :plan="planData"
                />
            </div>

            <answer-plan-questions
                v-model:question-form-data="questionsFormData"
                :current-enrollments="enrollmentInputFormData"
                :plan="planData"
                :contract="contractData"
                :show-invalid-questions="showErrors"
                :enrollment-constraints="enrollmentConstraints"
                :contract-constraints="contractConstraints"
                :class="
                    questionsFormDataInvalid
                        ? 'js-ParticipantDetailsFormError'
                        : ''
                "
                @update-pricing="pricingKey += 1"
            />

            <div
                v-if="
                    allowEmptyEnrollments &&
                    questionsFormData.contractQuestions.length === 0 &&
                    questionsFormData.enrollmentQuestions.length === 0
                "
            >
                {{ $t('purchaseContract.noParticipantDetailsRequired') }}
            </div>
        </div>
    </purchase-contract-section>

    <purchase-contract-section
        v-if="contractHasPricing && (isActiveSection || !isKiosk)"
    >
        <template #title>{{ $t('purchaseContract.pricing') }}</template>

        <preview-contract-pricing-table
            :is-loading="isFetching"
            :cart="cart"
        />
    </purchase-contract-section>

    <!--If the section is active, show our continue buttons-->
    <div
        v-if="isActiveSection"
        class="flex items-center justify-end gap-5 pt-5"
    >
        <div
            v-if="continueButtonErrorMessage"
            class="text-red-500 font-semibold"
        >
            {{ continueButtonErrorMessage }}
        </div>

        <prime-button
            v-if="showCompleteBookingButton"
            :loading="isUpdatingContract"
            :label="$t('purchaseContract.completeBooking')"
            :disabled="isFetching"
            @click="handleActivateContractNow"
        />

        <div
            v-else-if="showAddToCartCheckoutButtons"
            class="flex gap-4"
        >
            <prime-button
                :loading="isUpdatingContract"
                :disabled="isCreatingCheckout || isFetching"
                :label="
                    isEditing
                        ? $t('purchaseContract.saveChanges')
                        : $t('purchaseContract.addToCart')
                "
                @click="addToCartNoDocument"
            />
            <prime-button
                :loading="isCreatingCheckout"
                :disabled="isUpdatingContract || isFetching"
                :label="$t('purchaseContract.checkoutNow')"
                @click="handleCheckout"
            />
        </div>

        <prime-button
            v-else-if="showContinueButton"
            :label="$t('continue')"
            :loading="isUpdatingContract"
            :disabled="isFetching"
            @click="handleContinue"
        />

        <div v-else>{{ $t('somethingWrongWarning') }}</div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const ParticipantDetailsPlanFragment = graphql(/* GraphQL */ `
    fragment ParticipantDetailsPlanFragment on Plan {
        planType
        friendlyPlanType
        terms {
            allowEmptyEnrollments
            addressRequirement
            contractHolderMinimumAge
            askForEmailForGuestAtKiosk
            optionalEmailForGuestAtKiosk
            askForPhoneForGuestAtKiosk
            requiredPhoneForGuestAtKiosk
        }
        ...AnswerPlanQuestionsPlanFragment
        ...AddressInputPlanFragment
    }
`)

export const ParticipantDetailsContractFragment = graphql(/** GraphQL */ `
    fragment ParticipantDetailsContractFragment on PlanContract {
        ...EnrollmentCustomerPickerContractFragment
        ...ParticipantDetailsCompletedContractFragment
        ...AnswerPlanQuestionsPlanContractFragment
        ...EnrollmentConstraintPlanContractFragment
        ...ContractConstraintPlanContractFragment
        id
        draftExpiresAtUtc
        bookingDateUtc
        startDateLocal
        updatedAtUtc
        facility {
            id
            doNotMailDefaultValue
            ...CustomerInputFacilityFragment
        }
        customer {
            id
            firstName
            middleName
            lastName
            dateOfBirth
            email
            mobilePhone
        }
        terms {
            customer {
                firstName
                middleName
                lastName
                dateOfBirth
                email
                mobilePhone
                enrollmentMatch {
                    enrollmentTypeId
                    enrollmentIndex
                }
            }
        }
        isGuest
        enrollmentSpecifications {
            enrollmentType {
                id
                singular
                ageEnforcementMode
                ageMinimum
                ageMaximum
                requireContractHolder
            }
            count
            enrollments {
                customer {
                    id
                    commaName
                    firstName
                    middleName
                    lastName
                    dateOfBirth
                }
            }
        }
        documentTemplate {
            title
        }
        startSession {
            id
        }
        promotions {
            id
            code
            fullCode
        }
    }
`)
</script>

<script setup lang="ts">
import ClientOnly from '#app/components/client-only'
import PrimeButton from 'primevue/button'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import { type Ref, watch } from 'vue'
import { computed, onMounted, ref, watchEffect } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import AnswerPlanQuestions from '~/components/PurchaseContract/ParticipantDetails/PlanQuestions/AnswerPlanQuestions.vue'
import { getFragmentData } from '~/resources/graphql'
import type { QuestionFormData } from '~/composables/planQuestionFormData'
import { convertQuestionToProperty } from '~/composables/planQuestionFormData'
import type { AddressData } from '~/composables/addressData'
import {
    type EnrollmentInputArray,
    makeEmptyEnrollmentInput,
    makeNullEnrollmentInput
} from '~/composables/enrollment-input'
import PreviewContractPricingTable from '~/components/PurchaseContract/PreviewContractPricingTable.vue'
import { setCartVisible, useInvalidateCartQuery } from '~/composables/use-cart'
import { useUpdateContractCheckoutNowMutation } from '~/components/Checkout/create-checkout-mutation'
import { useRouter } from 'vue-router'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useRouteLink } from '~/composables/routing'
import type {
    ContractPropertyInput,
    EnrollmentInput,
    EnrollmentSlotInput,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import EnrollmentCustomerPicker from '~/components/PurchaseContract/CustomerPicker/EnrollmentCustomerPicker.vue'
import { parseUtc } from '~/composables/support/localization'
import { useShowAlert } from '~/composables/use-show-alert'
import { useContractExpiration } from '~/components/PurchaseContract/use-contract-expiration'
import type { EnrollmentInputEnrollment } from '~/composables/enrollment-input'
import ParticipantDetailsCompleted from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetailsCompleted.vue'
import AddressInputComponent from '~/components/PurchaseContract/ParticipantDetails/AddressInput.vue'
import { usePreviewSessionContractQuery } from '~/components/PurchaseContract/use-preview-session-contract-query'
import { Decimal } from '~/composables/support/decimal'
import { useLayoutMode } from '~/composables/use-layout-mode'
import CustomerInput, {
    type CustomerData
} from '~/components/PurchaseContract/ParticipantDetails/CustomerInput.vue'
import { doNotMailDefault } from '~/components/PurchaseContract/do-not-mail-helpers'
import { useI18n } from 'vue-i18n'
import { useQueryClient } from '@tanstack/vue-query'
import { useEnrollmentConstraint } from '~/components/PurchaseContract/ParticipantDetails/enrollment-constraint'
import { useContractConstraint } from '~/components/PurchaseContract/ParticipantDetails/contract-constraint'

const props = defineProps<{
    isActiveSection: boolean
    plan: FragmentType<typeof ParticipantDetailsPlanFragment>
    contract: FragmentType<typeof ParticipantDetailsContractFragment>
    isEditing: boolean
    isUpdatingContract: boolean
}>()

const emit = defineEmits<{
    (e: 'continue', payload: UpdateContractInput): void
    (e: 'addToCart', payload: UpdateContractInput): void
    (e: 'activateContractNow', payload: UpdateContractInput): void
    (e: 'editSection'): void
}>()

const root = ref<HTMLDivElement>()
onMounted(() => {
    if (props.isActiveSection) {
        root.value?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        })
    }
})

const { isKiosk } = useLayoutMode()
const { t } = useI18n()

const planData = computed(() =>
    getFragmentData(ParticipantDetailsPlanFragment, props.plan)
)

const allowEmptyEnrollments = computed(
    () => planData.value.terms.allowEmptyEnrollments
)
const addressRequired = computed(
    () =>
        planData.value.terms.addressRequirement !== 'NONE' &&
        planData.value.planType === 'AGREEMENT'
)

const contractData = computed(() =>
    getFragmentData(ParticipantDetailsContractFragment, props.contract)
)

const expirationDate = computed(() =>
    contractData.value.draftExpiresAtUtc === null
        ? null
        : parseUtc(contractData.value.draftExpiresAtUtc)
)
const { expirationPast, expirationRelativeString } =
    useContractExpiration(expirationDate)
let alertFired = false
const queryClient = useQueryClient()
watchEffect(() => {
    if (!alertFired && expirationPast.value) {
        queryClient.invalidateQueries({
            predicate: query => query.queryKey.includes('CourseCalendarQuery')
        })
        alertFired = true
        showAlert(t('oops'), t('purchaseContract.timeExpiredWarning'))
    }
})

const hasDocument = computed(() => contractData.value.documentTemplate !== null)

const enrollmentInputFormData = ref<EnrollmentInputArray>([])
const enrollmentInputInvalid = computed(
    () =>
        !allowEmptyEnrollments.value &&
        enrollmentInputFormData.value.some(value =>
            value.enrollments.some(enrollment => enrollment.invalid)
        )
)

const questionsFormData: Ref<QuestionFormData> = ref({
    contractQuestions: [],
    enrollmentQuestions: []
})
const questionsFormDataInvalid = computed(() => {
    let invalid = false
    questionsFormData.value.contractQuestions.forEach(question => {
        if (question.invalid) {
            invalid = true
            return
        }
    })

    questionsFormData.value.enrollmentQuestions.forEach(enrollmentFormData => {
        enrollmentFormData.questions.forEach(question => {
            if (question.invalid) {
                invalid = true
                return
            }
        })
    })

    return invalid
})

const customerFormData = ref<CustomerData>({
    enrollmentMatch: null,
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    doNotMail: false,
    mobilePhone: '',
    invalid: true
})

const customerFormDataInvalid = computed(
    () => collectCustomerInformation.value && customerFormData.value.invalid
)

const addressFormData = ref<AddressData>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    postal: '',
    country: '',
    invalid: true
})

const addressFormDataInvalid = computed(
    () => addressRequired.value && addressFormData.value.invalid
)

const {
    enrollmentConstraints,
    attachmentsAreInvalid,
    getAttachmentInput,
    resetAttachmentUploads
} = useEnrollmentConstraint(enrollmentInputFormData, contractData)

const {
    contractConstraints,
    attachmentsAreInvalid: contractAttachmentsAreInvalid,
    getAttachmentInput: getContractAttachmentInput,
    resetAttachmentUploads: resetContractAttachmentUploads
} = useContractConstraint(enrollmentInputFormData, contractData)

watch(
    contractData,
    newValue => {
        enrollmentInputFormData.value = newValue.enrollmentSpecifications.map(
            enrollmentSpecification => {
                let enrollments: EnrollmentInputEnrollment[] =
                    enrollmentSpecification.enrollments.map(enrollment => {
                        return {
                            data: {
                                type: 'existing',
                                id: enrollment.customer.id,
                                commaName: enrollment.customer.commaName,
                                firstName: enrollment.customer.firstName,
                                middleName: enrollment.customer.middleName,
                                lastName: enrollment.customer.lastName,
                                dateOfBirth:
                                    enrollment.customer.dateOfBirth ?? ''
                            },
                            invalid: false,
                            invalidFromConstraint: false
                        }
                    })

                // If we do not have a complete set of enrollments, pad the array with nulls
                // And set invalid to true
                if (enrollments.length < enrollmentSpecification.count) {
                    enrollments = enrollments.concat(
                        Array(
                            enrollmentSpecification.count - enrollments.length
                        )
                            .fill(null)
                            .map(() => {
                                if (newValue.isGuest) {
                                    return makeEmptyEnrollmentInput()
                                } else {
                                    return makeNullEnrollmentInput()
                                }
                            })
                    )
                }

                return {
                    enrollmentType: {
                        id: enrollmentSpecification.enrollmentType.id,
                        singular:
                            enrollmentSpecification.enrollmentType.singular,
                        ageEnforcementMode:
                            enrollmentSpecification.enrollmentType
                                .ageEnforcementMode,
                        ageMinimum:
                            enrollmentSpecification.enrollmentType.ageMinimum,
                        ageMaximum:
                            enrollmentSpecification.enrollmentType.ageMaximum,
                        requireContractHolder:
                            enrollmentSpecification.enrollmentType
                                .requireContractHolder
                    },
                    count: enrollmentSpecification.count,
                    enrollments: enrollments
                }
            }
        )

        // If contract data is guest checkout or already has customer information,
        // we need to pull from contract terms
        if (newValue.isGuest || newValue.terms.customer !== null) {
            if (
                newValue.terms.customer !== null &&
                newValue.terms.customer.enrollmentMatch !== null
            ) {
                const matchTypeId =
                    newValue.terms.customer.enrollmentMatch.enrollmentTypeId
                // We need to check that the enrollment still exists,
                // If you go from 2 adults to 1 it may no longer exist
                const existingCount = enrollmentInputFormData.value.find(
                    type => type.enrollmentType.id === matchTypeId
                )

                // If the count still exists, load the data
                if (
                    existingCount !== undefined &&
                    existingCount.count >=
                        newValue.terms.customer.enrollmentMatch
                            .enrollmentIndex +
                            1
                ) {
                    customerFormData.value = {
                        enrollmentMatch: {
                            enrollmentTypeId:
                                newValue.terms.customer.enrollmentMatch
                                    .enrollmentTypeId,
                            enrollmentIndex:
                                newValue.terms.customer.enrollmentMatch
                                    .enrollmentIndex
                        },
                        firstName: newValue.terms.customer.firstName,
                        middleName: newValue.terms.customer.middleName,
                        lastName: newValue.terms.customer.lastName,
                        dateOfBirth: newValue.terms.customer.dateOfBirth ?? '',
                        email: newValue.terms.customer.email,
                        doNotMail: doNotMailDefault(
                            newValue.facility.doNotMailDefaultValue
                        ),
                        mobilePhone: newValue.terms.customer.mobilePhone,
                        // CustomerInput component will take care of updating this
                        invalid: true
                    }
                }
                // We can preserve the email/phone, but we should trash the name since it is
                // probably wrong
                else {
                    customerFormData.value = {
                        enrollmentMatch: null,
                        firstName: '',
                        middleName: '',
                        lastName: '',
                        dateOfBirth: '',
                        email: newValue.terms.customer.email,
                        doNotMail: doNotMailDefault(
                            newValue.facility.doNotMailDefaultValue
                        ),
                        mobilePhone: newValue.terms.customer.mobilePhone,
                        invalid: true
                    }
                }
            }
            // Just load any data we have - we don't use the contract customer because for guests
            // that is the online guest customer
            else {
                customerFormData.value = {
                    enrollmentMatch: null,
                    firstName: newValue.terms.customer?.firstName ?? '',
                    middleName: newValue.terms.customer?.middleName ?? '',
                    lastName: newValue.terms.customer?.lastName ?? '',
                    dateOfBirth: newValue.terms.customer?.dateOfBirth ?? '',
                    email: newValue.terms.customer?.email ?? '',
                    doNotMail: doNotMailDefault(
                        newValue.facility.doNotMailDefaultValue
                    ),
                    mobilePhone: newValue.terms.customer?.mobilePhone ?? '',
                    // CustomerInput component will take care of updating this
                    invalid: true
                }
            }
        } else {
            customerFormData.value = {
                enrollmentMatch: null,
                firstName: newValue.customer.firstName,
                middleName: newValue.customer.middleName,
                lastName: newValue.customer.lastName,
                dateOfBirth: newValue.customer.dateOfBirth ?? '',
                email: newValue.customer.email,
                doNotMail: doNotMailDefault(
                    newValue.facility.doNotMailDefaultValue
                ),
                mobilePhone: newValue.customer.mobilePhone,
                invalid: true
            }
        }

        // Reset the attachment uploads - these should be uploaded to server so now part of contract data
        resetAttachmentUploads()
        resetContractAttachmentUploads()
    },
    { immediate: true }
)

const continueButtonErrorMessage = computed(() => {
    if (
        showErrors.value &&
        (customerFormDataInvalid.value ||
            addressFormDataInvalid.value ||
            questionsFormDataInvalid.value ||
            enrollmentInputInvalid.value ||
            attachmentsAreInvalid.value ||
            contractAttachmentsAreInvalid.value)
    ) {
        return t('purchaseContract.pleaseFixErrorsAbove')
    }

    return undefined
})

const showErrors = ref(false)
const showAlert = useShowAlert()
function getUpdateContractInput(): UpdateContractInput | undefined {
    let hasInvalidInput = false

    if (enrollmentInputInvalid.value) {
        hasInvalidInput = true
    }

    if (customerFormDataInvalid.value) {
        hasInvalidInput = true
    }

    if (addressFormDataInvalid.value) {
        hasInvalidInput = true
    }

    if (questionsFormDataInvalid.value) {
        hasInvalidInput = true
    }

    if (attachmentsAreInvalid.value) {
        hasInvalidInput = true
    }

    if (contractAttachmentsAreInvalid.value) {
        hasInvalidInput = true
    }

    // If invalid inputs exist, scroll up and return
    if (hasInvalidInput) {
        const invalidInputs = document.querySelectorAll(
            '.js-ParticipantDetailsFormError'
        )
        if (invalidInputs.length > 0) {
            invalidInputs[0].scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        }
        showErrors.value = true
        return undefined
    }

    const enrollments: EnrollmentInput[] = enrollmentInputFormData.value.map(
        enrollmentSpecification => {
            const enrollments: EnrollmentSlotInput[] =
                enrollmentSpecification.enrollments
                    .map(enrollment => {
                        if (enrollment.data === null) {
                            return null
                        } else if (enrollment.data.type === 'existing') {
                            return {
                                customerId: enrollment.data.id
                            }
                        } else {
                            return {
                                newCustomer: {
                                    firstName: enrollment.data.firstName,
                                    lastName: enrollment.data.lastName,
                                    middleName: enrollment.data.middleName,
                                    linkCustomer:
                                        enrollment.data.householdMember,
                                    dateOfBirth: enrollment.data.dateOfBirth
                                }
                            }
                        }
                    })
                    .filter(value => value !== null)

            return {
                enrollmentTypeId: enrollmentSpecification.enrollmentType.id,
                count: enrollmentSpecification.count,
                enrollments
            }
        }
    )

    let subtotalPrice = '0'
    let totalPrice = '0'
    if (subtotal.value !== undefined) {
        subtotalPrice = subtotal.value
    }

    if (total.value !== undefined) {
        totalPrice = total.value
    }

    showErrors.value = false
    return {
        id: contractData.value.id,
        updatedAt: contractData.value.updatedAtUtc,
        enrollments,
        properties: propertiesInputArray.value,
        subtotal: subtotalPrice,
        total: totalPrice,
        address: addressRequired.value
            ? {
                  address1: addressFormData.value.address1,
                  address2: addressFormData.value.address2,
                  city: addressFormData.value.city,
                  state: addressFormData.value.state,
                  postal: addressFormData.value.postal,
                  country: addressFormData.value.country
              }
            : undefined,
        customer: collectCustomerInformation.value
            ? {
                  enrollmentMatch: customerFormData.value.enrollmentMatch,
                  firstName: customerFormData.value.firstName,
                  middleName: customerFormData.value.middleName,
                  lastName: customerFormData.value.lastName,
                  dateOfBirth: customerFormData.value.dateOfBirth,
                  email: customerFormData.value.email,
                  doNotMail: customerFormData.value.doNotMail,
                  mobilePhone: customerFormData.value.mobilePhone
              }
            : undefined,
        attachments: [...getAttachmentInput(), ...getContractAttachmentInput()]
    }
}

async function handleContinue() {
    const updateContractInput = getUpdateContractInput()
    if (updateContractInput === undefined) {
        return
    }

    if (planData.value.planType === 'AGREEMENT' && !hasDocument.value) {
        throw new Error('All agreements should have a document')
    }

    emit('continue', updateContractInput)
}

async function addToCartNoDocument() {
    const updateContractInput = getUpdateContractInput()
    if (updateContractInput === undefined) {
        return
    }

    emit('addToCart', updateContractInput)
}

async function handleActivateContractNow() {
    const updateContractInput = getUpdateContractInput()
    if (updateContractInput === undefined) {
        return
    }

    emit('activateContractNow', updateContractInput)
}

const invalidateCartQuery = useInvalidateCartQuery()
const router = useRouter()
const activeFacility = useActiveFacility()
const { facilityCheckoutLink } = useRouteLink()
const { mutate: updateContractCheckoutNow, isPending: isCreatingCheckout } =
    useUpdateContractCheckoutNowMutation()
async function handleCheckout() {
    const updateContractInput = getUpdateContractInput()
    if (updateContractInput === undefined) {
        return
    }

    updateContractCheckoutNow(updateContractInput, {
        onSuccess: data => {
            // If update successful
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // If create successful - go to checkout
                if (data.createCheckout.__typename === 'CreateCheckoutResult') {
                    router.push(
                        facilityCheckoutLink(
                            activeFacility.value.slug,
                            data.createCheckout.recordId
                        )
                    )
                    return
                } else if (
                    data.createCheckout.__typename ===
                    'CreateCheckoutIncomplete'
                ) {
                    showAlert(
                        t('headsUp'),
                        t(
                            'purchaseContract.contractsNotReadyForPurchaseWarning',
                            {
                                count: data.createCheckout.count
                            }
                        )
                    )
                    invalidateCartQuery()
                    setCartVisible(true)
                    return
                }
                // otherwise update cart and display errors
                else {
                    invalidateCartQuery()
                }
            }

            throw new Error()
        }
    })
}

const propertiesInputArray = computed<ContractPropertyInput[]>(() => {
    return [
        ...questionsFormData.value.contractQuestions.map(
            convertQuestionToProperty
        ),
        ...questionsFormData.value.enrollmentQuestions.flatMap(
            enrollmentQuestion => {
                return enrollmentQuestion.questions.map(question => {
                    return {
                        ...convertQuestionToProperty(question),
                        enrollmentSpecification: {
                            enrollmentTypeId:
                                enrollmentQuestion.enrollmentSpecification
                                    .enrollmentTypeId,
                            sequence:
                                enrollmentQuestion.enrollmentSpecification
                                    .sequence
                        }
                    }
                })
            }
        )
    ]
})

const pricingKey = ref(0)
// We do not care about enrollments for pricing, just the slots
const enrollmentTypeCounts = computed(() =>
    enrollmentInputFormData.value.map(enrollmentSpecification => {
        return {
            enrollmentTypeId: enrollmentSpecification.enrollmentType.id,
            count: enrollmentSpecification.count
        }
    })
)

const { cart, isFetching, subtotal, total } = usePreviewSessionContractQuery(
    computed(() => contractData.value.startSession?.id ?? null),
    computed(() => contractData.value.startDateLocal),
    enrollmentTypeCounts,
    computed(() =>
        contractData.value.promotions.map(promotion => ({
            contractPromotionId: promotion.id,
            code: promotion.code,
            fullCode: promotion.fullCode,
            description: '' // Leave blank because we don't actually need a description here
        }))
    ),
    propertiesInputArray,
    pricingKey
)

const contractHasPricing = computed(() => {
    if (total.value === undefined) {
        return false
    }
    return new Decimal(total.value).isNotZero()
})

// If contract has no pricing and no document, we show a button 'Complete Booking'
// Which will activate the contract immediately
const showCompleteBookingButton = computed(
    () => !contractHasPricing.value && !hasDocument.value && !isKiosk.value
)

// If contract has pricing and no document, we show two buttons 'Add to Cart' and 'Checkout'
const showAddToCartCheckoutButtons = computed(
    () => contractHasPricing.value && !hasDocument.value && !isKiosk.value
)

// If contract has a document section we show a 'Continue' button
const showContinueButton = computed(() => isKiosk.value || hasDocument.value)

// We collect customer information if:
// 1) no pricing (agreements and $0 booking)
// 2) guest (we may need customer information for document so just always collect it for simplicity)
const collectCustomerInformation = computed(() => {
    return !contractHasPricing.value || contractData.value.isGuest
})
</script>
